import React, { memo, useMemo } from 'react';
import classNames from 'classnames';

import { useIntl } from '../../util/reactIntl';
import { propTypes } from '../../util/types';

import { Avatar, ReviewRating, UserDisplayName } from '../../components';

import css from './Reviews.module.css';
import ReactCountryFlag from 'react-country-flag';
const Review = memo(props => {
  const { review, intl, country } = props;

  const date = review.attributes.createdAt;
  const dateString = useMemo(
    () => intl.formatDate(date, { month: 'long', day: 'numeric', year: 'numeric' }),
    [date, intl]
  );
  const countryIso2 = useMemo(() => (country || '').trim().toUpperCase(), [country]);
  return (
    <div className={css.reviewWrapper}>
      <div className={css.reviewHeader}>
        <div className={css.reviewAuthor}>
          <div className={css.reviewAuthorInfo}>
            <Avatar className={css.avatar} user={review.author} />
            <div className={css.reviewRating}>
              <div>
                <UserDisplayName user={review.author} intl={intl} className={css.reviewAuthorName}/>
                {countryIso2 && (
                  <span className={css.countryFlag}>
                    <ReactCountryFlag
                      countryCode={countryIso2}
                      svg
                      className={css.flagIcon}
                      aria-label={countryIso2}
                    />
                  </span>
                )}
              </div>
              <ReviewRating
                rating={review.attributes.rating}
                className={css.desktopReviewRating}
                reviewStarClassName={css.reviewRatingStar}
              />
            </div>
          </div>

        </div>
        <div className={css.reviewDate}>
          {dateString}
        </div>
      </div>

      <div className={css.reviewContentWrapper}>
        {/* content */}
        <p className={css.reviewContent}>{review.attributes.content}</p>
      </div>

    </div>
  );
});

Review.displayName = 'Review';

/**
 * A component that renders a list of reviews.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {Array<propTypes.review>} props.reviews - The reviews to render
 * @returns {JSX.Element}
 */
const Reviews = props => {
  const intl = useIntl();
  const { className, rootClassName, reviews = [], country } = props;
  const classes = classNames(rootClassName || css.root, className);

  return reviews.length ? (
    <ul className={classes}>
      {reviews.map(r => {
        return (
          <li key={`Review_${r.id.uuid}`} className={css.reviewItem}>
            <Review review={r} intl={intl} country={country} />
          </li>
        );
      })}
    </ul>
  ) : null;
};

export default Reviews;
